import React, { useEffect, useState } from "react";
import { Plus, Save, X, Trash2, Edit3, Eye, EyeOff, ExternalLink } from "lucide-react";
import {
  adminListPages, adminCreatePage, adminUpdatePage, adminDeletePage, adminGetPage,
} from "../../lib/api";

function Field({ label, value, onChange, type = "text", textarea, rows = 4, mono }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="block">
      <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">{label}</span>
      <Comp
        type={type}
        value={value || ""}
        rows={textarea ? rows : undefined}
        onChange={(e) => onChange(e.target.value)}
        className={`mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#FF6B3D]/50 focus:ring-2 focus:ring-[#FF6B3D]/20 ${mono ? "font-mono" : ""}`}
      />
    </label>
  );
}

export default function PagesPanel({ onToast }) {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const blank = {
    slug: "", title: "", content: "", meta_title: "", meta_description: "",
    published: true, show_in_footer: false, footer_column: "Company", order: 0,
  };
  const [form, setForm] = useState(blank);

  const load = async () => setList(await adminListPages());
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(blank); setEditing("new"); };
  const startEdit = async (p) => {
    const full = await adminGetPage(p.id);
    setForm(full);
    setEditing(p.id);
  };
  const cancel = () => { setEditing(null); setForm(blank); };

  const save = async () => {
    try {
      if (!form.slug || !form.title) {
        onToast?.("Slug and title required", "error");
        return;
      }
      if (editing === "new") {
        await adminCreatePage(form);
        onToast?.("Page created");
      } else {
        // Send the whole form including slug — backend enforces uniqueness.
        await adminUpdatePage(editing, form);
        onToast?.("Page saved");
      }
      await load();
      cancel();
    } catch (e) {
      onToast?.(e?.response?.data?.detail || "Save failed", "error");
    }
  };

  const remove = async (id, title) => {
    if (!window.confirm(`Delete page "${title}"?`)) return;
    try {
      await adminDeletePage(id);
      onToast?.("Deleted");
      await load();
    } catch (e) {
      onToast?.("Delete failed", "error");
    }
  };

  const toggle = async (p) => {
    try {
      await adminUpdatePage(p.id, { published: !p.published });
      await load();
    } catch (e) {
      onToast?.("Toggle failed", "error");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="lm-font font-extrabold text-[#11151C] text-2xl">Pages</h2>
          <p className="text-[#11151C]/60 text-sm">Manage About, Contact, Legal pages and create your own. Content uses simple Markdown.</p>
        </div>
        <button onClick={startNew} className="lm-btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
          <Plus className="w-4 h-4" /> New Page
        </button>
      </div>

      {editing && (
        <div className="mt-5 border border-[#11151C]/10 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-bold text-[#11151C]">{editing === "new" ? "New Page" : "Edit Page"}</h3>
            <button onClick={cancel}><X className="w-4 h-4" /></button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Slug (URL path)" value={form.slug}
              onChange={(v) => setForm({ ...form, slug: v.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
            <Field label="Title" value={form.title} onChange={(v) => setForm({ ...form, title: v })} />
            <Field label="Meta Title (SEO)" value={form.meta_title} onChange={(v) => setForm({ ...form, meta_title: v })} />
            <Field label="Meta Description (SEO)" value={form.meta_description} onChange={(v) => setForm({ ...form, meta_description: v })} />
            <label className="block">
              <span className="text-xs font-semibold text-[#11151C]/70 uppercase tracking-wider">Footer column</span>
              <select value={form.footer_column || ""}
                onChange={(e) => setForm({ ...form, footer_column: e.target.value })}
                className="mt-1 w-full border border-[#11151C]/15 rounded-xl px-3 py-2 text-sm">
                <option value="">(none)</option>
                <option value="Product">Product</option>
                <option value="Company">Company</option>
                <option value="Support">Support</option>
                <option value="Legal">Legal</option>
              </select>
            </label>
            <Field label="Order" type="number" value={form.order}
              onChange={(v) => setForm({ ...form, order: Number(v) || 0 })} />
            <label className="inline-flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })} />
              <span className="text-sm font-semibold">Published</span>
            </label>
            <label className="inline-flex items-center gap-2 mt-6">
              <input type="checkbox" checked={!!form.show_in_footer}
                onChange={(e) => setForm({ ...form, show_in_footer: e.target.checked })} />
              <span className="text-sm font-semibold">Show in footer</span>
            </label>
          </div>
          <div className="mt-3">
            <Field label="Content (Markdown)" textarea rows={16} mono value={form.content}
              onChange={(v) => setForm({ ...form, content: v })} />
            <p className="text-[11px] text-[#11151C]/50 mt-1">
              Supports: ## Headings, **bold**, lists (- or 1.), [link text](/path-or-url), &gt; blockquote.
            </p>
          </div>
          <button onClick={save} className="lm-btn-primary mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold">
            <Save className="w-4 h-4" /> Save
          </button>
        </div>
      )}

      <div className="mt-6 border border-[#11151C]/10 rounded-2xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#FFFBF6]">
            <tr>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Slug</th>
              <th className="text-left p-3">Footer</th>
              <th className="text-left p-3">Published</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-[#11151C]/50">No pages yet.</td></tr>
            )}
            {list.map((p) => (
              <tr key={p.id} className="border-t border-[#11151C]/5">
                <td className="p-3 font-semibold">{p.title}</td>
                <td className="p-3 text-xs font-mono text-[#11151C]/70">/{p.slug}</td>
                <td className="p-3 text-xs">{p.show_in_footer ? <span className="text-[#11151C]">{p.footer_column}</span> : <span className="text-[#11151C]/40">No</span>}</td>
                <td className="p-3">
                  <button onClick={() => toggle(p)} className={`text-xs font-bold px-2 py-1 rounded inline-flex items-center gap-1 ${
                    p.published ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                  }`}>
                    {p.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                    {p.published ? "Live" : "Draft"}
                  </button>
                </td>
                <td className="p-3 text-right whitespace-nowrap">
                  <a href={`/${p.slug}`} target="_blank" rel="noopener noreferrer" title="View" className="p-1.5 hover:bg-[#FFE6D6] rounded-lg inline-flex"><ExternalLink className="w-4 h-4 text-[#11151C]" /></a>
                  <button onClick={() => startEdit(p)} className="p-1.5 hover:bg-[#FFE6D6] rounded-lg"><Edit3 className="w-4 h-4 text-[#11151C]" /></button>
                  <button onClick={() => remove(p.id, p.title)} className="p-1.5 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4 text-red-500" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
